import { useCallback, useEffect, useMemo, useState } from 'react'

import {
  DSButton,
  DSDropdown,
  DSIcon,
  DSPagination,
  Table as DSTable,
  TableAlign
} from '@vr/ds-react'

import ModalFiles from '../modal-files'
import ModalError from '@/components/modal-error'
import EditableText from '@/compositions/editable-text'
import { TableActionsDropdown } from '@/compositions/table-actions-dropdown'
import { COLLECT_LIST_COLUMNS } from '@/constants/collect-list-columns'
import { useCollectContext } from '@/contexts/collect.context'
import { useUnitsListCollect } from '@/hooks/use-units-list-collect'
import { downloadFile } from '@/utils/download-file'

export const CollectUnitsList = ({ collect }: { collect: any }) => {
  const [isModalFilesOpen, setIsModalFilesOpen] = useState(false)
  const [errorModal, setErrorModal] = useState(false)

  const { collectRefetchTrigger } = useCollectContext()

  const {
    handleLimitChange: onLimitChange,
    limit,
    refetch,
    results,
    setPage,
    tableData
  } = useUnitsListCollect(collect)

  useEffect(() => {
    if (collectRefetchTrigger > 0) {
      refetch()
    }
  }, [collectRefetchTrigger])

  const handleClick = () => {
    setIsModalFilesOpen(true)
  }

  const handleDownloadIntegrationFile = useCallback(() => {
    if (!collect?.arquivoDeIntegracao) {
      setErrorModal(true)
      return
    }

    const url = collect?.arquivoDeIntegracao
    downloadFile(url, `integracao-${collect.id}.txt`, 'txt')
  }, [collect])

  const mappedData = useMemo(() => {
    return tableData.map(item => {
      return {
        ...item
      }
    })
  }, [tableData])

  const handleLimitChange = useCallback(
    (selectedOptions: string | number | (string | number)[]) => {
      let value: string | number | undefined
      if (Array.isArray(selectedOptions)) {
        value = selectedOptions[0]
      } else {
        value = selectedOptions
      }
      const newLimit =
        typeof value === 'number' ? value : Number.parseInt(value, 10)
      onLimitChange(newLimit)
    },
    [onLimitChange]
  )

  return (
    <>
      <DSTable css={{ width: '100%', marginTop: '20px' }}>
        <DSTable.Head>
          <DSTable.Cell colSpan={6} align='right'>
            <>
              <DSButton
                variant='ghost'
                size='small'
                outlined
                css={{ marginRight: '$sm16' }}
                onClick={handleClick}
              >
                Vincular cartões por arquivo
              </DSButton>
              <DSButton
                variant='ghost'
                size='small'
                outlined
                css={{ marginRight: '$sm16' }}
                onClick={handleDownloadIntegrationFile}
                iconLeft={'principal-download'}
              >
                Baixar arquivo de integração
              </DSButton>
              <DSDropdown
                options={[
                  { label: '10' },
                  { label: '25' },
                  { label: '50' },
                  { label: '100' }
                ]}
                onSelectionChange={handleLimitChange}
                css={{ padding: '16px 0' }}
              >
                <DSButton
                  variant='secondary'
                  size='small'
                  iconRight='principal-chevron-down'
                >
                  {`Mostrar: ${limit}`}
                </DSButton>
              </DSDropdown>
            </>
          </DSTable.Cell>
        </DSTable.Head>
        <DSTable.Head>
          {COLLECT_LIST_COLUMNS.map(item => (
            <DSTable.Cell
              key={item.field}
              id={item.field}
              align={item.align as TableAlign}
            >
              {item.headerName}
            </DSTable.Cell>
          ))}
        </DSTable.Head>
        <DSTable.Body>
          {mappedData.map(item => (
            <DSTable.Row key={item.id} id={item.id?.toString()}>
              {COLLECT_LIST_COLUMNS.map(key => {
                let cellContent: any
                if (key.field === 'acoes') {
                  cellContent = (
                    <TableActionsDropdown
                      pageResource='collect'
                      unit={item}
                      files={item.urlArquivosCartaColeta}
                      id={collect.id}
                    />
                  )
                } else if (key.field === 'vinculo') {
                  cellContent = (
                    <DSIcon
                      name={
                        item.numeroCartao
                          ? 'principal-check'
                          : 'principal-error'
                      }
                      color={
                        item.numeroCartao
                          ? 'successVeryDark'
                          : 'warningVeryDark'
                      }
                    />
                  )
                } else if (key.field === 'numeroCartao') {
                  cellContent = (
                    <EditableText unit={item} collectId={collect.id} />
                  )
                } else if (['nomeUnidade', 'nomeColaborador'].includes(key.field)) {
                  cellContent = (
                    <span style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                      {item[key.field as keyof typeof item]}
                    </span>
                  )
                } else {
                  cellContent = item[key.field as keyof typeof item]
                }
                return (
                  <DSTable.Cell
                    key={key.field}
                    align={key.align as TableAlign}
                  >
                    {cellContent}
                  </DSTable.Cell>
                )
              })}
            </DSTable.Row>
          ))}
        </DSTable.Body>
      </DSTable>

      <DSPagination
        total={results}
        maxPerPage={limit}
        handleChange={(newPage: number) => setPage(newPage)}
        nextPreview
        siblingCount={1}
      />

      <ModalFiles
        isOpen={isModalFilesOpen}
        onClose={() => setIsModalFilesOpen(false)}
        collectId={collect.id}
      />

      <ModalError isOpen={errorModal} onClose={() => setErrorModal(false)}>
        Arquivo de integração não encontrado ou inválido.
      </ModalError>
    </>
  )
}
